import React from 'react';
import Section from '../../components/common/Section';
import { Card, Button } from '../../components/common';

/**
 * 价格套餐特性接口
 */
interface PricingFeature {
  id: string;
  text: string;
  included: boolean | 'partial' | 'limited'; // true, false, 'partial'(部分支持), 'limited'(有限制)
  tooltip?: string; // 提示信息
}

/**
 * 价格套餐接口
 */
interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  features: PricingFeature[];
  isPopular?: boolean;
  ctaText: string;
  ctaLink: string;
}

/**
 * 价格方案页面组件
 * 展示不同的价格套餐和功能对比
 * @returns {React.ReactElement} 渲染的React组件
 */
const PricingPage: React.FC = () => {
  // 状态管理
  const [hoveredPlan, setHoveredPlan] = React.useState<string | null>(null);
  
  // 价格套餐数据
  const pricingPlans: PricingPlan[] = [
    {
      id: 'basic',
      name: '基础版',
      description: '适合个人开发者和小型团队使用',
      price: 699,
      currency: '¥',
      isPopular: false,
      ctaText: '开始使用',
      ctaLink: '/contact',
      features: [
        { id: 'f1', text: '微信小程序商家端 + 用户端', included: true },
        { id: 'f2', text: '基础商品管理功能', included: true },
        { id: 'f3', text: '基础订单管理功能', included: true },
        { id: 'f4', text: '门店营业时间设置', included: true },
        { id: 'f5', text: '基础支付能力（微信支付）', included: true },
        { id: 'f6', text: '分享商品功能', included: true}
      ]
    },
    {
      id: 'pro',
      name: '专业版',
      description: '适合中小型企业和成长中的团队',
      price: 1299,
      currency: '¥',
      isPopular: true,
      ctaText: '免费试用14天',
      ctaLink: '/contact',
      features: [
        { id: 'f1', text: '限时折扣、满减、优惠券', included: true },
        { id: 'f2', text: '会员等级系统', included: true },
        { id: 'f3', text: '储值与积分功能', included: true },
        { id: 'f4', text: '自定义装修页面', included: true },
        { id: 'f5', text: '运营活动数据看板', included: true },
        { id: 'f6', text: '商品云图库', included: true },
      ]
    },
    {
      id: 'enterprise',
      name: '企业版',
      description: '适合大型企业和有特殊需求的团队',
      price: 1899,
      currency: '¥',
      isPopular: false,
      ctaText: '开始使用',
      ctaLink: '/contact',
      features: [
        { id: 'f1', text: '多门店管理与切换', included: true },
        { id: 'f2', text: '分店独立数据统计', included: true },
        { id: 'f3', text: '权限角色设置（店长/店员）', included: true },
        { id: 'f4', text: 'CRM 客户运营模块', included: true },
        { id: 'f5', text: '高级数据导出与分析', included: true },
        { id: 'f6', text: '支持私有化部署（可选）', included: true },
      ]
    }
  ];

  /**
   * 渲染功能支持状态图标
   * @param {boolean | 'partial' | 'limited'} included - 功能支持状态
   * @param {string} tooltip - 提示信息
   * @returns {React.ReactElement} 渲染的图标
   */
  const renderFeatureStatus = (included: boolean | 'partial' | 'limited', tooltip?: string): React.ReactElement => {
    if (included === true) {
      return (
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10">
          <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else if (included === 'partial' || included === 'limited') {
      return (
        <div className="group relative">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100">
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
          {tooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none shadow-lg z-50">
              {tooltip}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-t-0 border-l-4 border-r-4 border-l-transparent border-r-transparent border-t-8 border-t-gray-900"></div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
  };

  return (
    <div className="pricing-page">
      {/* 页面标题区域 */}
      <Section 
        background="background"
        title="价格套餐"
        subtitle="选择最适合您业务需求的套餐，所有套餐均包含核心功能"
        spacing="large"
      >
        {/* 价格卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto px-4 sm:px-6 lg:px-0">
          {pricingPlans.map((plan) => (
            <div 
              key={plan.id} 
              className={`transition-all duration-300 transform ${
                hoveredPlan === plan.id ? '-translate-y-2 shadow-xl' : ''
              } ${
                plan.isPopular 
                  ? 'relative z-10 sm:col-span-2 lg:col-span-1 lg:scale-105 order-first lg:order-none' 
                  : ''
              }`}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
            >
              <Card 
                className={`overflow-hidden h-full ${plan.isPopular ? 'shadow-lg ring-2 ring-primary' : 'shadow-md hover:shadow-lg'}`} 
                padding="none" 
                radius="large"
              >
                {plan.isPopular && (
                  <div className="bg-primary text-white text-xs font-bold uppercase tracking-wider py-2 text-center">
                    最受欢迎
                  </div>
                )}
                <div className="p-8">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.isPopular ? 'text-primary' : 'text-secondary'}`}>{plan.name}</h3>
                  <p className="text-gray-500 text-sm mb-6 h-12">{plan.description}</p>
                  <div className="flex items-baseline mb-8 justify-center">
                    <span className="text-3xl font-bold">{plan.currency}</span>
                    <span className={`text-6xl font-extrabold ${plan.isPopular ? 'text-primary' : ''}`}>{plan.price}</span>
                    <span className="text-gray-500 ml-2 text-lg">/年</span>
                  </div>
                  <Button 
                    to={plan.ctaLink} 
                    fullWidth
                    variant={plan.isPopular ? 'primary' : 'ghost'}
                    className={
                      plan.isPopular 
                        ? 'hover:bg-primary/90 hover:shadow-md' 
                        : hoveredPlan === plan.id
                          ? 'bg-primary/10 text-primary hover:bg-primary/20 hover:shadow-sm'
                          : 'bg-gray-100 text-secondary hover:bg-gray-200 hover:shadow-sm'
                    }
                    rightIcon={
                      <svg 
                        className={`w-5 h-5 transform transition-transform duration-300 ${hoveredPlan === plan.id ? 'translate-x-1' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    }
                  >
                    {plan.ctaText}
                  </Button>
                </div>
                <div className="border-t border-gray-100 p-8 bg-gray-50">
                  <h4 className="font-semibold text-gray-800 mb-4 text-center">包含功能</h4>
                  <ul className="space-y-4">
                    {plan.features.map((feature) => (
                      <li key={feature.id} className="flex items-center">
                        <div className="mr-3 flex-shrink-0">
                          {renderFeatureStatus(feature.included, feature.tooltip)}
                        </div>
                        <span className="text-gray-700">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </Section>

      {/* 常见问题区域 */}
      <Section 
        title="常见问题"
        subtitle="关于我们价格套餐的常见问题解答"
        spacing="large"
        background="background"
      >
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6">
            <Card padding="large" radius="large" className="hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">Q</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3 text-secondary">如何选择适合我的套餐？</h3>
                  <p className="text-gray-600 leading-relaxed">您可以根据您的团队规模、项目数量和功能需求来选择合适的套餐。基础版适合个人开发者和小型团队，专业版适合中小型企业，企业版则适合大型企业和有特殊需求的团队。</p>
                </div>
              </div>
            </Card>
            
            <Card padding="large" radius="large" className="hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">Q</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3 text-secondary">我可以随时更换套餐吗？</h3>
                  <p className="text-gray-600 leading-relaxed">是的，您可以随时升级或降级您的套餐。升级时，我们会按比例计算剩余时间的费用差额；降级则会在当前计费周期结束后生效。</p>
                </div>
              </div>
            </Card>
            
            <Card padding="large" radius="large" className="hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">Q</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3 text-secondary">是否提供自定义套餐？</h3>
                  <p className="text-gray-600 leading-relaxed">对于有特殊需求的企业客户，我们提供定制化的解决方案。请联系我们的销售团队，我们将根据您的具体需求提供个性化的报价。</p>
                </div>
              </div>
            </Card>
            
            <Card padding="large" radius="large" className="hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">Q</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3 text-secondary">如何获取发票？</h3>
                  <p className="text-gray-600 leading-relaxed">所有套餐均可开具增值税专用发票或普通发票。您可以在支付后通过客户中心申请开票，我们会在3个工作日内处理您的开票申请。</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Section>

      {/* 联系我们区域 */}
      <Section 
        background="primary"
        spacing="large"
      >
        <div className="text-center text-white px-4 sm:px-6 py-8 sm:py-12 rounded-3xl max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-left mb-8 md:mb-0 md:mr-8">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">还有其他问题？</h2>
              <p className="text-white/90 text-lg max-w-2xl leading-relaxed">
                如果您对我们的价格套餐有任何疑问，或者需要更多信息，请随时联系我们的客户服务团队。我们将竭诚为您服务！
              </p>
            </div>
            <div className="flex-shrink-0">
              <Button 
                to="/contact" 
                size="large"
                className="bg-white text-primary hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:-translate-y-1 border-none"
                rightIcon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                }
              >
                联系我们
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default PricingPage;